import React, { useMemo } from 'react'

import { Section, SectionHeading, Card } from '../../../components/ui'
import { addOnSectionsFrom } from '../../../lib/service-catalogue'
import { usePublishedCatalogue } from '../../../lib/use-published-catalogue'
import { whatsappChatProps } from '../../../components/whatsapp-chat'
import { BeforeAfter } from '../../../components/before-after'
import { LABELS } from './catalogue'

import './extras.css'

// THE ADD-ONS.
//
// The home page states that anything can go on any wash and links here, so
// this is where all the priced rows legitimately live. The job is to make a
// long list readable, not to make it short: a customer who has scrolled past
// the comparison table has already chosen a wash and is now asking whether we
// also do the thing their car actually needs.
//
// THE LIST IS /api/v2/services AND NOTHING IN THIS DIRECTORY DECIDES IT.
// Until 5 October 2026 ./catalogue.js held 21 entries, each naming a service
// by its exact database name, and that array was what the page advertised.
// Nine services had been retired in the database the day before. The page went
// on offering them with a permanent blank where the price should be, because
// there is no fallback price by design. The list and the database were two
// lists.
//
// The endpoint now publishes where every row belongs as well as what it costs:
// `section` (outside the car or inside it), `page_rank` (the last 24 months of
// paid orders, highest first) and `on_prices_page`. All three are decided in
// app/services/customer_catalogue.rb, which is also what step one of the
// booking form reads, so the page and the form cannot disagree about what we
// sell. ../../../lib/service-catalogue.js turns the payload into the two
// groups below and drops anything it cannot price.
//
// WHAT CHANGED FROM THE OLD BLOCK.
//
//   1. Grouped by whether the work happens outside the car or inside it,
//      rather than by how the business thinks about them. "Popular extras"
//      told a customer nothing about their own car, and the old "Specialist
//      extras" heading used a word DESIGN.md §3 bans outright. The four
//      groups that replaced it on 5 October were still three distinctions
//      more than anybody standing at their car makes. The cut, the headings
//      and the order inside each group are now server side.
//   2. The "Detailing request?" WhatsApp link stays, as a plain --accent
//      link. No glyph: --whatsapp is the glyph colour and nothing else (§1),
//      and §3 bans the floating WhatsApp button this link used to echo. It
//      hangs off the outside group, directly under the clay bar and polish
//      rows it follows from.
//   3. The biohazard bundle is gone, with its group. Nothing in the catalogue
//      is priced against other services any more, so the component that
//      carried it, the "Save £X" sentence computed from the bundle's DB
//      `excludes`, and the .svc-bundle rules in extras.css all went rather
//      than being left behind as dead code for the next bundle that may
//      never come.
//
// TWO CARDS, EACH OVER TWO COLUMNS FROM 960PX. With four groups the section
// was two rows of two cards, and the grid did the splitting. Two groups
// cannot fill that: a 13-row card beside an 8-row card leaves about 210px of
// bare stone under the shorter one at 1440, and two cards side by side is the
// only arrangement two cards get. So the cards go full width, stacked, and
// the splitting moves inside them. ./extras.css carries the measurements at
// six widths and why the split waits for the 960px breakpoint rather than
// starting at 600px, where two columns are 243px wide and most of the names
// wrap.
//
// The halves are two <ul>s rather than one list flowed into two columns, so
// that each column starts flush under the heading with no stray rule above
// it, the break lands exactly where splitIntoColumns puts it, and the order
// reads down the first column and then down the second. The rows are ordered
// by what people buy, and a ranked list read left to right across two columns
// is not a ranked list.
//
// WHAT SHOULD PROBABLY STOP BEING SOLD is a server decision and it is in the
// build report, not here. The short version: the child seat and push chair
// cleans between them took single-figure conversions from 1,759 paid clicks,
// and the ones still listed are the next rows a retirement takes. Taking one
// off this page is a line in CustomerCatalogue::PAGE_SUPPRESSED; taking it off
// the site is `active: false` in the services table. Neither is an edit here.

const ExtraRow = ({ item }) => (
  <li className="svc-extra-row">
    <span className="svc-extra-name">{item.label}</span>
    {/* No price skeleton. A row we cannot price is not in the list at all,
        which is the bug this page was rebuilt around: the old version printed
        the name and then an empty box, for ever. */}
    <span className="svc-extra-price">{item.price}</span>
  </li>
)

// The first column takes the odd row, so 13 reads 7 then 6 rather than 6 then
// 7: the longer column is the one the eye starts in.
const splitIntoColumns = items => {
  const first = Math.ceil(items.length / 2)
  return [items.slice(0, first), items.slice(first)]
}

const ExtraGroup = ({ group }) => (
  <Card as="article" className="svc-extra-card">
    <h3 className="svc-extra-title">{group.title}</h3>

    <div className="svc-extra-cols">
      {splitIntoColumns(group.items)
        .filter(column => column.length > 0)
        .map(column => (
          <ul className="svc-extra-list" key={column[0].key}>
            {column.map(item => (
              <ExtraRow key={item.key} item={item} />
            ))}
          </ul>
        ))}
    </div>

    {group.id === 'outside' && (
      <p className="svc-extra-note">
        Detailing, paint restoration or something bespoke?{' '}
        <a className="gwmc-link" {...whatsappChatProps("Hi GoWashMyCar! I'd like to ask about a detailing job")}>
          Get a quote on WhatsApp.
        </a>
      </p>
    )}
  </Card>
)

// WHILE THE FETCH IS IN FLIGHT, AND IF IT NEVER LANDS.
//
// This section is below the comparison table and below the fold, so the fetch
// is not in front of the LCP and a skeleton here costs the page nothing. What
// it must not be is an empty card under a heading that says "Add anything to
// any wash": a visitor reads that as a list of nothing.
//
// Ten blank rows, which is about the height of the shorter of the two real
// cards, so the arrival pushes the page down a little rather than a lot.
const PENDING_ROWS = Array.from({ length: 10 }, (_, index) => index)

const ExtrasPending = () => (
  <Card as="div" className="svc-extra-card" role="status" aria-live="polite">
    <span className="gwmc-sr-only">Loading our add-ons and their prices.</span>
    <ul className="svc-extra-list">
      {PENDING_ROWS.map(index => (
        <li className="svc-extra-row" key={index}>
          <span className="svc-extra-name svc-extra-name--pending" aria-hidden="true" />
          <span className="svc-extra-price svc-extra-price--pending" aria-hidden="true" />
        </li>
      ))}
    </ul>
  </Card>
)

// If the catalogue does not come back, or comes back with nothing in it, say
// so and hand over the one route to a human who can price the job. Silence
// here would read as "we do not do add-ons", which is both untrue and the
// worse of the two outcomes.
//
// It covers both cases on purpose, and the wording is true of both: "we cannot
// show them" says nothing about why. A line claiming the fetch failed would be
// a lie on the day the fetch succeeds and returns an empty catalogue.
const ExtrasUnavailable = () => (
  <p className="svc-extra-fail">
    We cannot show our add-on prices just now, which is us and not you. Reload the page, or{' '}
    <a className="gwmc-link" {...whatsappChatProps("Hi GoWashMyCar! I'd like to ask what an add-on costs")}>
      ask us on WhatsApp
    </a>{' '}
    and we will tell you what it costs.
  </p>
)

export const Extras = () => {
  // This section owns the fetch on /services: it is the one that cannot render
  // at all without the endpoint, and exactly one section per page may ask, or
  // two mounts in the same commit both fire the request. See
  // ../../../lib/use-published-catalogue.js.
  const { entries, status } = usePublishedCatalogue()

  const groups = useMemo(() => addOnSectionsFrom(entries, { labels: LABELS }), [entries])

  return (
    <Section id="extras" className="svc-extras">
      <SectionHeading intro="Add any of these to your booking.">
        Upgrade any wash
      </SectionHeading>

      {/* Proof first: three add-ons, before and after, each with its price
          and a "Book this" that lands with it ticked. Directly under the
          comparison table, so the visitor who has just picked a wash sees
          what the extras do (components/before-after). */}
      <div className="svc-extra-pairs">
        <BeforeAfter source="before_after_services" />
      </div>

      <div className="svc-extra-grid">
        {groups.length > 0 ? (
          groups.map(group => <ExtraGroup key={group.id} group={group} />)
        ) : status === 'loading' ? (
          <ExtrasPending />
        ) : (
          <ExtrasUnavailable />
        )}
      </div>
    </Section>
  )
}
